/**
 * Shadow root management for Caddie widget
 * Provides global access to shadow root for portals and other components
 */

let shadowRootInstance: ShadowRoot | null = null;
let shadowHostInstance: HTMLDivElement | null = null;

/**
 * Initialize shadow root with CDN styles
 */
export function initShadowRoot(
  cdnUrl: string,
  storeId?: string | null
): ShadowRoot {
  const shadowHost = document.createElement('div');
  shadowHost.style.cssText =
    'position:fixed;inset:0;pointer-events:none;z-index:20';
  const shadowRoot = shadowHost.attachShadow({ mode: 'open' });

  // Inject widget stylesheet into shadow root (not <head>) for CSS isolation
  const cssLink = document.createElement('link');
  cssLink.rel = 'stylesheet';
  cssLink.href = `${cdnUrl}/caddie-ui.css`;
  shadowRoot.appendChild(cssLink);

  if (storeId) {
    const customStylesLink = document.createElement('link');
    customStylesLink.rel = 'stylesheet';
    customStylesLink.href = `${cdnUrl}/custom-styles/${storeId}.css`;
    shadowRoot.appendChild(customStylesLink);
  }

  // Fonts must stay in <head> — they are global and don't load from shadow roots
  const fontLink = document.createElement('link');
  fontLink.rel = 'stylesheet';
  fontLink.href =
    'https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700&display=swap';
  document.head.appendChild(fontLink);

  const mountPoint = document.createElement('div');
  mountPoint.id = 'caddie-root';
  mountPoint.style.pointerEvents = 'none';
  shadowRoot.appendChild(mountPoint);

  document.body.appendChild(shadowHost);

  // Store references globally
  shadowRootInstance = shadowRoot;
  shadowHostInstance = shadowHost;

  return shadowRoot;
}

/**
 * Get the current shadow root instance
 */
export function getShadowRoot(): ShadowRoot | null {
  return shadowRootInstance;
}

/**
 * Get the shadow host element
 */
export function getShadowHost(): HTMLDivElement | null {
  return shadowHostInstance;
}

/**
 * Get the correct portal target for React portals
 * Returns shadow root if available, otherwise falls back to #caddie-root or document.body
 */
export function getPortalTarget(): Element | DocumentFragment {
  // If we have a shadow root instance, use it
  if (shadowRootInstance) {
    const shadowCaddieRoot = shadowRootInstance?.getElementById('caddie-root');
    if (shadowCaddieRoot) {
      return shadowCaddieRoot;
    }
    return shadowRootInstance;
  }

  // Fallback: try to find caddie-root and check if it's in a shadow root
  const caddieRoot = document.getElementById('caddie-root');
  if (caddieRoot) {
    const rootNode = caddieRoot.getRootNode();
    if (rootNode instanceof ShadowRoot) {
      return rootNode;
    }
    return caddieRoot;
  }

  // Final fallback
  return document.body;
}

/**
 * Cleanup shadow root on unmount
 */
export function cleanupShadowRoot(): void {
  if (shadowHostInstance && shadowHostInstance.parentNode) {
    shadowHostInstance.parentNode.removeChild(shadowHostInstance);
  }
  shadowRootInstance = null;
  shadowHostInstance = null;
}
